import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { MapPin, User, Phone, Building2, FileText, CheckCircle, AlertCircle, Calendar, Clock, Plane, Plus, X, ChevronUp, ChevronDown, Car, Luggage } from 'lucide-react'
import { MonthDayPicker } from '@/components/ui/month-day-picker'
import { TimePicker } from '@/components/ui/time-picker'
import AddressAutocomplete from '@/components/address/AddressAutocomplete'

interface TenantBookingInfo {
  tenant_name: string
  vehicle_categories: string[]
  payment_methods: string[]
  enable_extended_categories?: boolean
}

const ORDER_CATEGORIES = ['送機', '接機', '其他']
const ORDER_CATEGORIES_EXTENDED = ['送機', '接機', '單程', '包車']

const AIRPORTS = [
  '桃園機場一航廈',
  '桃園機場二航廈',
  '松山機場',
  '台中機場',
  '高雄機場',
  '基隆港',
]

const getTodayDate = () => {
  const today = new Date()
  return `${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`
}

const formatToISO8601 = (dateStr: string, timeStr: string) => {
  const currentYear = new Date().getFullYear()
  const [month, day] = dateStr.split('-')
  const [hours, minutes] = timeStr.split(':')
  return `${currentYear}-${month}-${day}T${hours}:${minutes}:00`
}

const inputCls = (hasError?: boolean) =>
  `w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300 bg-white ${hasError ? 'border-red-400' : 'border-gray-300'}`

export default function TenantBookingPage() {
  const { token } = useParams<{ token: string }>()
  const [info, setInfo] = useState<TenantBookingInfo | null>(null)
  const [loadError, setLoadError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [orderNumber, setOrderNumber] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [extraStops, setExtraStops] = useState<{ address: string; name: string; phone: string }[]>([])

  const [form, setForm] = useState({
    company_name: '',
    tax_id: '',
    contact_person: '',
    contact_phone: '',
    order_category: '送機',
    vehicle_type: '',
    passenger_count: 1,
    luggage_large: 0,
    luggage_small: 0,
    pickup_date: getTodayDate(),
    pickup_time: '',
    pickup_location: '',
    dropoff_location: '',
    flight_number: '',
    flight_time: '',
    driver_notes: '',
  })

  useEffect(() => {
    if (!token) return
    apiClient.get(`/public/tenant-booking/${token}`)
      .then(res => {
        setInfo(res.data)
        if (res.data.vehicle_categories?.length > 0) {
          setForm(f => ({ ...f, vehicle_type: res.data.vehicle_categories[0] }))
        }
      })
      .catch(() => setLoadError('此連結無效或已失效，請聯絡服務人員'))
  }, [token])

  const set = (field: string, value: string | number) =>
    setForm(f => ({ ...f, [field]: value }))

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.contact_person.trim()) e.contact_person = '請填寫聯絡人'
    if (!form.contact_phone.trim()) e.contact_phone = '請填寫聯絡電話'
    if (!form.pickup_date) e.pickup_date = '請選擇日期'
    if (!form.pickup_time) e.pickup_time = '請選擇時間'
    if (!form.pickup_location.trim()) e.pickup_location = '請填寫上車地點'
    if (!form.dropoff_location.trim()) e.dropoff_location = '請填寫下車地點'
    if (!form.vehicle_type) e.vehicle_type = '請選擇車型'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      const isAirport = form.order_category === '送機' || form.order_category === '接機'
      const payload = {
        company_name: form.company_name || undefined,
        tax_id: form.tax_id || undefined,
        contact_person: form.contact_person,
        contact_phone: form.contact_phone,
        order_category: form.order_category,
        vehicle_type: form.vehicle_type,
        passenger_count: Number(form.passenger_count),
        pickup_datetime: formatToISO8601(form.pickup_date, form.pickup_time),
        pickup_location: form.pickup_location,
        dropoff_location: form.dropoff_location,
        additional_stops: extraStops.filter(s => s.address.trim()).map((s, i) => ({
          address: s.address, order: i + 1,
          name: s.name.trim() || undefined,
          phone: s.phone.trim() || undefined,
        })),
        driver_notes: [
          (Number(form.luggage_large) > 0 ? `大行李 ${form.luggage_large} 件` : ''),
          (Number(form.luggage_small) > 0 ? `小行李 ${form.luggage_small} 件` : ''),
          form.driver_notes,
        ].filter(Boolean).join('、') || undefined,
        flight_number: isAirport && form.flight_number ? form.flight_number : undefined,
        arrival_time: isAirport && form.flight_time ? form.flight_time : undefined,
      }
      const res = await apiClient.post(`/public/tenant-booking/${token}`, payload)
      setOrderNumber(res.data.order_number)
      setSubmitted(true)
    } catch (err: any) {
      setErrors({ submit: err?.response?.data?.detail || '預約失敗，請稍後再試' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-sm text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-gray-600 text-sm">{loadError}</p>
        </div>
      </div>
    )
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-sm text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="font-bold text-gray-800 mb-1">預約已送出</p>
          <p className="text-sm text-gray-500 mb-1">我們將盡快與您確認行程</p>
          <p className="text-xs text-gray-400 font-mono">#{orderNumber}</p>
        </div>
      </div>
    )
  }

  const pickupIsAirport = form.order_category === '接機'
  const dropoffIsAirport = form.order_category === '送機'

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
          <p className="text-xs text-gray-400 mb-1">{info.tenant_name}</p>
          <h1 className="text-lg font-bold text-gray-800">填寫預約</h1>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
          {/* 接送類別 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">接送類別</label>
            <div className="flex bg-gray-100 p-1 rounded-lg">
              {(info?.enable_extended_categories ? ORDER_CATEGORIES_EXTENDED : ORDER_CATEGORIES).map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => {
                    set('order_category', cat)
                    set('pickup_location', '')
                    set('dropoff_location', '')
                    setExtraStops([])
                  }}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                    form.order_category === cat ? 'bg-[#2563EB] text-white' : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* 公司名稱 | 統編（選填）*/}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Building2 className="w-4 h-4 inline mr-1" />公司名稱（選填）
              </label>
              <input className={inputCls()} value={form.company_name}
                onChange={e => set('company_name', e.target.value)} placeholder="公司名稱" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">統編（選填）</label>
              <input className={inputCls()} value={form.tax_id}
                onChange={e => set('tax_id', e.target.value)} placeholder="統一編號" />
            </div>
          </div>

          {/* 日期 | 時間 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Calendar className="w-4 h-4 inline mr-1" />日期 <span className="text-red-500">*</span>
              </label>
              <MonthDayPicker value={form.pickup_date} onChange={v => set('pickup_date', v)} placeholder="選擇日期" />
              {errors.pickup_date && <p className="text-red-500 text-xs mt-1">{errors.pickup_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Clock className="w-4 h-4 inline mr-1" />時間 <span className="text-red-500">*</span>
              </label>
              <TimePicker value={form.pickup_time} onChange={v => set('pickup_time', v)} placeholder="選擇時間" />
              {errors.pickup_time && <p className="text-red-500 text-xs mt-1">{errors.pickup_time}</p>}
            </div>
          </div>

          {/* 聯絡人 | 電話 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <User className="w-4 h-4 inline mr-1" />聯絡人 <span className="text-red-500">*</span>
              </label>
              <input className={inputCls(!!errors.contact_person)} value={form.contact_person}
                onChange={e => set('contact_person', e.target.value)} placeholder="聯絡人姓名" />
              {errors.contact_person && <p className="text-red-500 text-xs mt-1">{errors.contact_person}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Phone className="w-4 h-4 inline mr-1" />聯絡電話 <span className="text-red-500">*</span>
              </label>
              <input className={inputCls(!!errors.contact_phone)} value={form.contact_phone}
                onChange={e => set('contact_phone', e.target.value)} placeholder="0912-345-678" />
              {errors.contact_phone && <p className="text-red-500 text-xs mt-1">{errors.contact_phone}</p>}
            </div>
          </div>

          {/* 上車地點 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <MapPin className="w-4 h-4 inline mr-1" />上車地點 <span className="text-red-500">*</span>
            </label>
            {pickupIsAirport ? (
              <select value={form.pickup_location} onChange={e => set('pickup_location', e.target.value)}
                className={inputCls(!!errors.pickup_location)}>
                <option value="">選擇機場</option>
                {AIRPORTS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            ) : (
              <AddressAutocomplete placeholder="請輸入上車地址" value={form.pickup_location}
                onChange={v => set('pickup_location', v)}
                className={errors.pickup_location ? 'border-red-400' : ''} />
            )}
            {errors.pickup_location && <p className="text-red-500 text-xs mt-1">{errors.pickup_location}</p>}
          </div>

          {/* 附加乘客（中途停靠點，可各自填姓名/電話，選填） */}
          {extraStops.map((stop, idx) => (
            <div key={idx} className="flex gap-2 items-start bg-gray-50 rounded-xl p-3">
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <MapPin className="w-4 h-4 inline mr-1" />附加乘客 {extraStops.length > 1 ? idx + 1 : ''}
                </label>
                <AddressAutocomplete
                  value={stop.address}
                  onChange={v => {
                    const updated = [...extraStops]
                    updated[idx] = { ...updated[idx], address: v }
                    setExtraStops(updated)
                  }}
                  placeholder="請輸入地址"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputCls()} value={stop.name}
                    onChange={e => {
                      const updated = [...extraStops]
                      updated[idx] = { ...updated[idx], name: e.target.value }
                      setExtraStops(updated)
                    }}
                    placeholder="姓名（選填）" />
                  <input className={inputCls()} value={stop.phone}
                    onChange={e => {
                      const updated = [...extraStops]
                      updated[idx] = { ...updated[idx], phone: e.target.value }
                      setExtraStops(updated)
                    }}
                    placeholder="電話（選填）" />
                </div>
              </div>
              <div className="flex flex-col gap-1 mt-6 shrink-0">
                <button type="button" disabled={idx === 0}
                  onClick={() => {
                    const updated = [...extraStops]
                    ;[updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]]
                    setExtraStops(updated)
                  }}
                  className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button type="button" disabled={idx === extraStops.length - 1}
                  onClick={() => {
                    const updated = [...extraStops]
                    ;[updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]]
                    setExtraStops(updated)
                  }}
                  className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => setExtraStops(extraStops.filter((_, i) => i !== idx))}
                  className="p-1.5 rounded-md bg-red-50 text-red-400 hover:bg-red-100 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}

          <button type="button" onClick={() => setExtraStops([...extraStops, { address: '', name: '', phone: '' }])}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-blue-300 text-blue-500 text-sm hover:bg-blue-50 transition-colors">
            <Plus className="w-4 h-4" />新增附加乘客
          </button>

          {/* 下車地點 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <MapPin className="w-4 h-4 inline mr-1" />下車地點 <span className="text-red-500">*</span>
            </label>
            {dropoffIsAirport ? (
              <select value={form.dropoff_location} onChange={e => set('dropoff_location', e.target.value)}
                className={inputCls(!!errors.dropoff_location)}>
                <option value="">選擇機場</option>
                {AIRPORTS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            ) : (
              <AddressAutocomplete placeholder="請輸入下車地址" value={form.dropoff_location}
                onChange={v => set('dropoff_location', v)}
                className={errors.dropoff_location ? 'border-red-400' : ''} />
            )}
            {errors.dropoff_location && <p className="text-red-500 text-xs mt-1">{errors.dropoff_location}</p>}
          </div>

          {/* 車型 | 人數 | 行李大 | 行李小 */}
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Car className="w-4 h-4 inline mr-1" />車型 <span className="text-red-500">*</span>
              </label>
              <select value={form.vehicle_type} onChange={e => set('vehicle_type', e.target.value)}
                className={inputCls(!!errors.vehicle_type)}>
                {info.vehicle_categories.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              {errors.vehicle_type && <p className="text-red-500 text-xs mt-1">{errors.vehicle_type}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <User className="w-4 h-4 inline mr-1" />人數
              </label>
              <input className={inputCls()} type="number" min={1} max={20} value={form.passenger_count}
                onChange={e => set('passenger_count', Number(e.target.value))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Luggage className="w-4 h-4 inline mr-1" />行李大
              </label>
              <input className={inputCls()} type="number" min={0} max={20} value={form.luggage_large}
                onChange={e => set('luggage_large', Number(e.target.value))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Luggage className="w-4 h-4 inline mr-1" />行李小
              </label>
              <input className={inputCls()} type="number" min={0} max={20} value={form.luggage_small}
                onChange={e => set('luggage_small', Number(e.target.value))} />
            </div>
          </div>

          {/* 航班資訊（送機/接機） */}
          {(form.order_category === '送機' || form.order_category === '接機') && (
            <div className="bg-blue-50 rounded-xl p-4 space-y-3">
              <p className="text-sm font-medium text-blue-700">
                <Plane className="w-4 h-4 inline mr-1" />航班資訊（選填）
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">航班編號</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-300"
                    value={form.flight_number} onChange={e => set('flight_number', e.target.value)} placeholder="例：CI100" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    {form.order_category === '接機' ? '抵達時間' : '起飛時間'}
                  </label>
                  <TimePicker value={form.flight_time} onChange={v => set('flight_time', v)} placeholder="選擇時間" />
                </div>
              </div>
            </div>
          )}

          {/* 備註 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <FileText className="w-4 h-4 inline mr-1" />備註
            </label>
            <textarea value={form.driver_notes} onChange={e => set('driver_notes', e.target.value)}
              placeholder="其他需求或注意事項..." rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300 resize-none" />
          </div>

          {errors.submit && <p className="text-red-500 text-sm">{errors.submit}</p>}

          <button type="submit" disabled={submitting}
            className="w-full bg-[#2563EB] hover:bg-blue-700 text-white font-medium py-2.5 rounded-xl transition-colors disabled:opacity-60">
            {submitting ? '送出中...' : '送出預約'}
          </button>
        </form>
      </div>
    </div>
  )
}
